import { Controller } from "@hotwired/stimulus";

import Chart from 'chart.js/auto';
import ChartDataLabels from "chartjs-plugin-datalabels"
Chart.register(ChartDataLabels);

Chart.defaults.plugins.datalabels.display = function(ctx) {
  var opts = ctx.chart.options.plugins.datalabels;
  return opts && opts.hideWhenZero
    ? ctx.dataset.data[ctx.dataIndex] !== 0
    : undefined;
};

// Card #3807 follow-up: Jay wants bar labels drawn white and inside the bar
// by default, falling back to a dark label sitting just outside the bar when
// the segment is too small to fit white text legibly. This is set on
// Chart.defaults (not a per-chart option), so it reaches every Chart.js
// instance in WIN -- including Janela's own bar/line panes on /triage, which
// never configure datalabels themselves (see the janela gem's own
// chart_controller.js). A chart or dataset that sets its own local
// `color`/`anchor`/`align` always wins over this default (Chart.js merges
// leaf values, most-specific wins) -- bar_chart_controller.js,
// distribution_chart_controller.js, distribution_trend_chart_controller.js,
// distribution_trend_chart_modal_controller.js, chart_preview_controller.js
// and doughnut_chart_controller.js all already set their own colour, so none
// of them are affected by this change. hub/objective_chart_controller.js's
// bar chart and objective_mini_chart_controller.js's bar branch do NOT set a
// local override, so this default now reaches them too -- both are small
// tiles, which is exactly the "bar too small" branch this is built for.
//
// Returning `undefined` for anything that isn't a bar (doughnut, pie, line)
// leaves the plugin's own built-in default in place (grey/dark centred
// text), i.e. unchanged from before -- this only ever touches bar charts.
//
// Chart.js resolves scriptable plugin options through two different call
// sites: chartjs-plugin-datalabels' own per-label draw pass (a proper
// Context with `chart`/`dataset`/`datasetIndex`/`dataIndex`), and Chart.js
// core's own generic options resolver, which walks the same
// `Chart.defaults.plugins.datalabels` functions with a coarser, dataset-level
// context that has no `dataset`/`dataIndex` at all. Every helper below has to
// tolerate the coarse call (returning a safe "not a fit" default) rather than
// assume the rich one -- confirmed by a 500 in manual testing when this
// wasn't guarded (Cannot read properties of undefined (reading 'type')).
function resolvedChartType(ctx) {
  var dataset = ctx && ctx.dataset;
  var chart = ctx && ctx.chart;
  return (dataset && dataset.type) || (chart && chart.config && chart.config.type);
}

function isBarContext(ctx) {
  return resolvedChartType(ctx) === 'bar';
}

function barLabelFitsInside(ctx) {
  if (!ctx || !ctx.chart || typeof ctx.datasetIndex !== 'number' || typeof ctx.dataIndex !== 'number') {
    return false;
  }

  var meta = ctx.chart.getDatasetMeta(ctx.datasetIndex);
  var element = meta && meta.data && meta.data[ctx.dataIndex];
  if (!element) return false;

  // Chart.js flips which prop is the bar's "length" (the dimension a label
  // has to fit inside) depending on orientation: for a vertical/column bar
  // it's .height (base to value), for a horizontal bar it's .width.
  var horizontal = ctx.chart.options.indexAxis === 'y';
  var barLength = Math.abs((horizontal ? element.width : element.height) || 0);

  // A label needs roughly its own font size plus a little padding either
  // side to sit inside the bar without crowding its edges.
  var fontSize = (Chart.defaults.font && Chart.defaults.font.size) || 12;
  return barLength >= fontSize + 10;
}

Chart.defaults.plugins.datalabels.color = function(ctx) {
  if (!isBarContext(ctx)) return undefined;
  return barLabelFitsInside(ctx) ? '#ffffff' : '#374151';
};

Chart.defaults.plugins.datalabels.anchor = function(ctx) {
  return isBarContext(ctx) ? 'end' : undefined;
};

Chart.defaults.plugins.datalabels.align = function(ctx) {
  if (!isBarContext(ctx)) return undefined;
  return barLabelFitsInside(ctx) ? 'start' : 'end';
};

// Without clamp, an 'end'-anchored/'end'-aligned label on a very small bar
// can be pushed past the chart's plot area and get clipped clean off --
// clamp pulls it back to stay within bounds instead of disappearing.
Chart.defaults.plugins.datalabels.clamp = function(ctx) {
  return isBarContext(ctx) ? true : undefined;
};

export default class extends Controller {
  static targets = ["canvas"];
  static values = {
    refreshUrl: String
  };

  get isDevEnvironment() {
    return document.head.querySelector("meta[name=rails_env]").content === "development"
  }

  connect() {
    console.log("Chart controller connected");
    this.fetchChartData()
    .then((chartData) => {
      if (chartData && chartData.data) {
        this.initializeChart(chartData);
      }
      // If no data yet (202 cold miss), startAutoRefresh will poll and init once ready
      this.startAutoRefresh();
    })
    .catch((error) => {
      console.error("Error fetching initial chart data:", error);
      this.startAutoRefresh();
    });
  }

  disconnect() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  async fetchChartData() {
    try {
      const response = await fetch(this.refreshUrlValue);
      // 202 means snapshot is being computed -- return null so caller skips init
      if (response.status === 202) {
        return null;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("Error fetching chart data:", error);
      throw error;
    }
  }

  initializeChart(chartData) {
    const ctx = this.canvasTarget.getContext("2d");

    this.chart = new Chart(ctx, {
      type: chartData.type,
      data: chartData.data,
      options: chartData.options
    });
  }

  startAutoRefresh() {
    if (this.isDevEnvironment) {
      console.log("Development environment detected, refreshing again in 1 minute...");
    }
    this.refreshInterval = setInterval(() => {
      this.refreshData();
    }, 60000);
  }

  async refreshData() {
    console.log("Refreshing chartJS data...");
    try {
      const chartData = await this.fetchChartData();
      if (!chartData || !chartData.data) {
        return; // Still computing -- next poll will pick it up
      }
      if (this.chart) {
        this.updateChart(chartData);
      } else {
        this.initializeChart(chartData);
      }
    } catch (error) {
      console.error("Error refreshing chart data:", error);
    }
  }

  updateChart(chart) {
    this.chart.type = chart.type;
    this.chart.data = chart.data;
    this.chart.update("none");
  }
}
